import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Expected,
  SeoItem,
  TYPE_LABEL,
  ThreadEntry,
  statusLabel,
} from '../types';
import AskBox from './AskBox';
import ItemDetail from './ItemDetail';
import RefreshDetail from './RefreshDetail';
import SnippetDetail from './SnippetDetail';
import {
  Section,
  VerdictChip,
  fmt,
  formatDay,
  formatTime,
  latestVerdict,
} from './common';

interface Props {
  item: SeoItem;
  actor: string;
  onApprove: (item: SeoItem) => Promise<void>;
  onReject: (item: SeoItem, reason: string) => Promise<void>;
  onRequestChanges: (item: SeoItem, feedback: string) => Promise<void>;
  onUndo: (item: SeoItem) => Promise<void>;
  onAsk: (item: SeoItem, question: string) => Promise<ThreadEntry[]>;
  onAddRule: (text: string) => Promise<void>;
  onOpenRun: (runId: string) => void;
}

const cell = { py: 0.75, verticalAlign: 'top' } as const;
const pct = (x: number) => `${(x * 100).toFixed(2)}%`;

// A title rewrite is judged on click-through rate, an article on clicks.
function ExpectedTable({ expected }: { expected: Expected }) {
  const ctr = expected.metric === 'page_ctr';
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={cell}>When</TableCell>
          <TableCell sx={cell}>
            {ctr ? 'Expected click-through' : 'Expected clicks'}
          </TableCell>
          <TableCell sx={cell}>Range</TableCell>
          <TableCell sx={cell}>
            {ctr
              ? 'As clicks at today’s impressions'
              : 'If Google ranks it about'}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {expected.checkpoints.map(c => (
          <TableRow key={c.day}>
            <TableCell sx={cell}>
              Day {c.day} ({c.window})
            </TableCell>
            <TableCell sx={{ ...cell, fontWeight: 600 }}>
              {ctr ? pct(c.mid) : `~${fmt(c.mid)}`}
            </TableCell>
            <TableCell sx={cell}>
              {ctr
                ? `${pct(c.low)} to ${pct(c.high)}`
                : `${fmt(c.low)}–${fmt(c.high)}`}
            </TableCell>
            <TableCell sx={cell}>
              {ctr && c.clicks
                ? `~${fmt(c.clicks.mid)} (${fmt(c.clicks.low)} to ${fmt(c.clicks.high)}) per 28 days`
                : `${c.position.mid} (range ${c.position.high}–${c.position.low})`}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// One decision record: what it saw, why, what changed, what it expects, when it
// checks and what it found — then the draft itself (with the approval buttons
// while it's waiting).
export default function DecisionDetail(props: Props) {
  const { item, onOpenRun } = props;
  const { opportunity: o, observed, change, expected } = item;
  const isRewrite = o.type === 'title_meta';
  // A merge is shown like a refresh, plus the pages it retires.
  const isRefresh = o.type === 'refresh' || o.type === 'merge';
  const legacy = !observed;

  return (
    <Stack spacing={2}>
      <Box>
        <Stack
          direction="row"
          spacing={1}
          sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}
        >
          <Chip size="small" label={TYPE_LABEL[o.type]} />
          <Chip size="small" variant="outlined" label={statusLabel(item)} />
          <VerdictChip verdict={latestVerdict(item)} />
        </Stack>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {o.type === 'new_article' ? `“${o.keyword}”` : o.articleTitle}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Proposed {formatTime(item.createdAt)}
          {item.runId && (
            <>
              {' · '}
              <Button
                size="small"
                sx={{ p: 0, minWidth: 0, verticalAlign: 'baseline' }}
                onClick={() => onOpenRun(item.runId!)}
              >
                see that day’s run report
              </Button>
            </>
          )}
        </Typography>
      </Box>

      {legacy && (
        <Alert severity="info">
          Drafted before the decision log existed, so it has no recorded
          observations, hypothesis or expected outcome.
        </Alert>
      )}

      {observed && (
        <Section
          title="What it saw"
          subtitle="The data that triggered this decision"
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            {observed.summary}
          </Typography>
          <Table size="small">
            <TableBody>
              {observed.facts.map(f => (
                <TableRow key={f.label}>
                  <TableCell sx={{ ...cell, width: '32%' }}>
                    {f.label}
                  </TableCell>
                  <TableCell sx={{ ...cell, fontWeight: 600 }}>
                    {f.value}
                  </TableCell>
                  <TableCell
                    sx={{ ...cell, color: 'text.secondary', width: '28%' }}
                  >
                    {f.source}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      )}

      {item.hypothesis && (
        <Section title="Its hypothesis" subtitle="Why it thinks this will help">
          <Typography variant="body2">{item.hypothesis}</Typography>
        </Section>
      )}

      {change && (
        <Section title="The change" subtitle={change.summary}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={cell} />
                <TableCell sx={cell}>Before</TableCell>
                <TableCell sx={cell}>After</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {change.fields.map(f => (
                <TableRow key={f.field}>
                  <TableCell sx={{ ...cell, width: '18%' }}>
                    {f.field}
                  </TableCell>
                  <TableCell sx={{ ...cell, color: 'text.secondary' }}>
                    {f.before || '—'}
                  </TableCell>
                  <TableCell sx={cell}>{f.after}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!!item.changeSummary?.length && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                What changed and why
              </Typography>
              <Stack component="ul" sx={{ m: 0, pl: 2 }} spacing={0.5}>
                {item.changeSummary.map(c => (
                  <Typography component="li" variant="body2" key={c}>
                    {c}
                  </Typography>
                ))}
              </Stack>
            </>
          )}
        </Section>
      )}

      {expected && (
        <Section
          title="Expected outcome"
          subtitle={
            expected.metric === 'page_ctr'
              ? `${expected.label}. Today: ${pct(expected.baseline)} (${fmt(expected.baselineClicks)} clicks in 28 days)`
              : `${expected.label}. Today: ${fmt(expected.baseline)}`
          }
        >
          <ExpectedTable expected={expected} />
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 1 }}
          >
            How this was estimated: {expected.method} “Worked” means at least
            the low end of the range; “didn’t work” needs Google to have shown
            the page at least 100 times, otherwise it’s “inconclusive”.
          </Typography>
        </Section>
      )}

      <Section
        title="Reviews"
        subtitle="Measured in Search Console after it goes live (data lags 2–3 days)"
      >
        {item.status === 'rejected' && (
          <Typography variant="body2" color="text.secondary">
            Not published, so there’s nothing to measure.
          </Typography>
        )}
        {item.status !== 'rejected' && !item.reviews.length && (
          <Typography variant="body2" color="text.secondary">
            Checks are scheduled for 14, 28 and 56 days after it goes live. The
            first verdict comes at day 28.
          </Typography>
        )}
        {!!item.reviews.length && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={cell}>Check</TableCell>
                <TableCell sx={cell}>Result</TableCell>
                <TableCell sx={cell}>Clicks</TableCell>
                <TableCell sx={cell}>Shown</TableCell>
                <TableCell sx={cell}>Position</TableCell>
                <TableCell sx={cell}>Orders</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {item.reviews.map(r => (
                <React.Fragment key={r.day}>
                  <TableRow>
                    <TableCell sx={cell}>
                      Day {r.day}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {formatDay(r.from)} – {formatDay(r.to)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cell}>
                      {r.status === 'scheduled' && (
                        <Typography variant="body2" color="text.secondary">
                          Due {formatDay(r.dueDate)}
                        </Typography>
                      )}
                      {r.status === 'cancelled' && (
                        <Typography variant="body2" color="text.secondary">
                          Cancelled (undone)
                        </Typography>
                      )}
                      {r.status === 'done' && (
                        <VerdictChip verdict={r.verdict} />
                      )}
                    </TableCell>
                    <TableCell sx={cell}>{fmt(r.actual?.clicks)}</TableCell>
                    <TableCell sx={cell}>
                      {fmt(r.actual?.impressions)}
                    </TableCell>
                    <TableCell sx={cell}>{fmt(r.actual?.position)}</TableCell>
                    <TableCell sx={cell}>
                      {r.actual
                        ? `${fmt(r.actual.orders)}${r.actual.orders ? ` ($${fmt(r.actual.revenueUsd)})` : ''}`
                        : '—'}
                    </TableCell>
                  </TableRow>
                  {r.note && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ ...cell, pt: 0, color: 'text.secondary' }}
                      >
                        {r.note}
                        {r.actual &&
                          ` All Dosaze pages together got ${fmt(r.actual.topicClicks)} clicks from these searches in this window.`}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      {!legacy && (
        <Section
          title="Rules it followed"
          subtitle="Standing rules active when it drafted this"
        >
          {item.rulesApplied.length ? (
            <Stack component="ul" sx={{ m: 0, pl: 2 }}>
              {item.rulesApplied.map(r => (
                <Typography component="li" variant="body2" key={r.id}>
                  {r.text}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No standing rules were active.
            </Typography>
          )}
        </Section>
      )}

      <Section
        title="Questions"
        subtitle="Ask why it did this, or tell it what to do differently"
      >
        <AskBox
          thread={item.thread}
          placeholder={
            isRewrite
              ? 'e.g. Why this wording? Why did you pick this article?'
              : isRefresh
                ? 'e.g. What did you cut, and why? Why do you expect it to climb?'
                : 'e.g. Why this topic instead of “mulberry silk”? Why do you expect so few clicks?'
          }
          onAsk={q => props.onAsk(item, q)}
          onAddRule={props.onAddRule}
        />
      </Section>

      {isRewrite ? (
        <Section title="How it looks in Google">
          <SnippetDetail
            item={item}
            onApprove={props.onApprove}
            onReject={props.onReject}
            onRequestChanges={props.onRequestChanges}
            onUndo={props.onUndo}
          />
        </Section>
      ) : isRefresh ? (
        <Section title={o.type === 'merge' ? 'The merge' : 'The refresh'}>
          <RefreshDetail
            item={item}
            onApprove={props.onApprove}
            onReject={props.onReject}
            onRequestChanges={props.onRequestChanges}
            onUndo={props.onUndo}
          />
        </Section>
      ) : (
        <Section title="The draft">
          <ItemDetail
            item={item}
            actor={props.actor}
            showTopic={legacy}
            onApprove={props.onApprove}
            onReject={props.onReject}
            onRequestChanges={props.onRequestChanges}
          />
        </Section>
      )}
    </Stack>
  );
}
